import { Spin } from "antd";
import { Plus, Package } from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetInventories } from "~/services/inventory";
import { useInventoryStore } from "../inventory.store";
import { ConsolidationActions } from "./consolidation-actions";
import { FilterInventories } from "./filter-inventories";
import { MetricInventories } from "./metric-inventories";
import { TableInventory } from "./table-inventory";
import { ImportInventoryModal } from "../import-inventory-modal";
import { InventoryStatusCode, DocumentType } from "~/utils/constants";
import QuickPkgButton from '../Quick-pkg/QuickPkgButton'; // Update this path to where you put the component
import { PendingImportFiles } from "../pending-import-files";
import { useGetFiles } from "~/services/file/file.queries";
import dayjs from "dayjs";


export const ListInventory = () => {
  const { filters, setFilters, openInventoryModal } = useInventoryStore();
  const [viewMode, setViewMode] = useState<"all" | "storage" | "pending-imports">("all");
  const navigate = useNavigate();

  // Make sure to destructure refetch from the hook
  const { data: inventories, isLoading, refetch } = useGetInventories({
    ...filters,
    ...(viewMode === "storage"
      ? { status: InventoryStatusCode.AVAILABLE_ON_HAND }
      : {}),
  });

  // Get pending import files count
  const { data: filesData } = useGetFiles(
    { type: DocumentType.INVENTORY, page: 1, pageSize: 50 },
    true
  );

  const pendingImportCount = useMemo(() => {
    if (!filesData?.items) return 0;
    
    // Load completed file IDs from localStorage
    let completedFileIds = new Set<string>();
    try {
      const stored = localStorage.getItem("completed_inventory_imports");
      if (stored) {
        const data = JSON.parse(stored);
        const thirtyDaysAgo = dayjs().subtract(30, 'days');
        Object.entries(data).forEach(([id, timestamp]: [string, any]) => {
          if (dayjs(timestamp).isAfter(thirtyDaysAgo)) {
            completedFileIds.add(id);
          }
        });
      }
    } catch (error) {
      console.error("Error loading completed imports:", error);
    }

    // Filter files to only show recent ones (last 30 days) and only files with valid size
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const allFiles = filesData.items.filter((file) => {
      if (!file.createdAt) return false;
      const fileDate = new Date(file.createdAt);
      const isRecent = fileDate >= thirtyDaysAgo;
      
      const hasValidSize = file.size && 
                           file.size.trim() !== "" && 
                           !isNaN(parseInt(file.size)) && 
                           parseInt(file.size) > 0;
      
      return isRecent && hasValidSize;
    });

    // Count pending files (not completed)
    return allFiles.filter((file) => !completedFileIds.has(file.id)).length;
  }, [filesData?.items]);

  return (
    <div className="bg-gray-50">
      <div className="max-w-full mx-auto">
        {/* Header and Title */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Inventory Management</h1>
          <div className="flex space-x-3">
            {/* Add the Quick Pkg button here */}
            <QuickPkgButton
              onSuccess={() => {
                refetch(); // This will refresh the inventory list
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm flex items-center space-x-2"
            />

            <button
              onClick={() => openInventoryModal()}
              className="cursor-pointer px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Create inventory</span>
            </button>
            <ImportInventoryModal />

            <button
              onClick={() => navigate("/dispatch/inventory-control")}
              className="cursor-pointer px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm flex items-center space-x-2"
            >
              <Package size={16} />
              <span>Inventory Control</span>
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-4 bg-white rounded-lg border p-2 shadow-sm">
          <div className="flex space-x-2">
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "all"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("all")}
            >
              All Inventory
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "storage"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("storage")}
            >
              Storage
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "pending-imports"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("pending-imports")}
            >
              Pending Imports
              {pendingImportCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {pendingImportCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Metrics Overview - Only show for inventory views */}
        {viewMode !== "pending-imports" && <MetricInventories />}

        {/* Content based on view mode */}
        {viewMode === "pending-imports" ? (
          <PendingImportFiles />
        ) : (
          <>
            {/* Search and Filter Bar */}
            <FilterInventories
              totalItems={inventories?.meta.totalItems ?? 0}
              currentItemsCount={inventories?.items?.length ?? 0}
            />

            {/* Inventory List */}
            <Spin spinning={isLoading} size="large">
              <TableInventory
                inventories={inventories?.items}
                pagination={{
                  current: filters.page,
                  pageSize: filters.pageSize,
                  total: inventories?.meta.totalItems ?? 0,
                  onChange: (page, pageSize) => {
                    setFilters({ ...filters, page, pageSize });
                  },
                  hideOnSinglePage: false,
                }}
              />
            </Spin>
          </>
        )}

        <ConsolidationActions />
      </div>
    </div>
  );
};